import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../translations/translations';
import { getLocalizedPlan } from '../../data/mock';

export const PricingPreviewSection = () => {
  const { language } = useLanguage();
  const startCorp = getLocalizedPlan('startCorp', language);
  const launchCorp = getLocalizedPlan('launchCorp', language);
  const runCorp = getLocalizedPlan('runCorp', language);

  return (
    <section className="py-20 bg-[#f5f2e8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-medium text-gray-900 mb-4">{t(language, 'sections.chooseRoute')}</h2>
          <p className="text-sm text-gray-500 mt-3">{t(language, 'sections.installmentsAvailable')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Incorporate */}
          <Link to="/start?package=incorporate" className="block group" data-testid="home-card-link-incorporate">
            <Card className="border-gray-200 group-hover:border-blue-900 hover:shadow-xl transition-all duration-300 h-full">
              <CardHeader>
                <CardTitle className="text-xl">{startCorp.name}</CardTitle>
                <CardDescription className="text-sm text-gray-500">{startCorp.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {startCorp.features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-2">
                      <Check className="w-4 h-4 text-blue-900 flex-shrink-0 mt-1" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-3xl font-bold text-gray-900">
                    ${startCorp.price} <span className="text-lg font-normal text-gray-500 align-text-bottom">USD</span> <span className="text-sm font-normal text-gray-500 align-text-bottom">{t(language, 'pricing.vat')}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{t(language, 'pricing.oneTimeFee')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Full Launch */}
          <Link to="/start?package=full-launch" className="block group relative" data-testid="home-card-link-full-launch">
            <Card className="border-2 border-blue-900 hover:shadow-xl transition-all duration-300 relative h-full">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--golden)', color: 'var(--midnight)' }}>
                  {t(language, 'pricing.mostPopular')}
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-xl">{launchCorp.name}</CardTitle>
                <CardDescription className="text-sm text-gray-500">{launchCorp.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  {launchCorp.includesIntro && (
                    <p className="text-sm text-gray-700 mb-3">{launchCorp.includesIntro}</p>
                  )}
                  <ul className="space-y-2">
                    {launchCorp.features.map((feature) => (
                      <li key={feature} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-blue-900 flex-shrink-0 mt-1" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-3xl font-bold text-gray-900">
                    ${launchCorp.price} <span className="text-lg font-normal text-gray-500 align-text-bottom">USD</span> <span className="text-sm font-normal text-gray-500 align-text-bottom">{t(language, 'pricing.vat')}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{t(language, 'pricing.oneTimeFee')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Corporate Compliance */}
          <Link to="/start?package=corporate-compliance" className="block group" data-testid="home-card-link-corporate-compliance">
            <Card className="border-gray-200 group-hover:border-blue-900 hover:shadow-xl transition-all duration-300 h-full">
              <CardHeader>
                <CardTitle className="text-xl">{runCorp.name}</CardTitle>
                <CardDescription className="text-sm text-gray-500">{runCorp.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  {runCorp.includesIntro && (
                    <p className="text-sm text-gray-700 mb-3">{runCorp.includesIntro}</p>
                  )}
                  <ul className="space-y-2">
                    {runCorp.features.map((feature) => (
                      <li key={feature} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-blue-900 flex-shrink-0 mt-1" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-3xl font-bold text-gray-900">
                    ${runCorp.setupPrice} <span className="text-lg font-normal text-gray-500 align-text-bottom">USD</span> <span className="text-sm font-normal text-gray-500 align-text-bottom">{t(language, 'pricing.vat')}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{t(language, 'pricing.upfrontSetup')}</p>
                  <p className="text-xl font-semibold text-blue-900 mt-3">
                    + ${runCorp.monthlyPrice} <span className="text-sm font-normal text-gray-500">USD</span>{t(language, 'pricing.perMonthSuffix')} <span className="text-xs font-normal text-gray-500">{t(language, 'pricing.vat')}</span>
                  </p>
                  <p className="text-sm text-gray-500">{t(language, 'pricing.monthlyPlan')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        <div className="text-center">
          <Link to="/start">
            <Button size="lg" className="shine-cta bg-blue-900 hover:bg-blue-800 text-white px-8">
              {t(language, 'nav.startCta')}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
